import { markRaw } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '../../../../utils/request.js';
import { splitSql } from '../../../../utils/sqlSplitter.js';

/** 单次执行最多允许的语句条数（防止粘贴超长脚本） */
export const MAX_STATEMENTS = 50;

export function summarizeResult(result) {
    if (!result) return '';
    if (result.type === 'QUERY') return (result.truncated ? '≥' : '') + result.rowCount + ' 行';
    if (result.type === 'UPDATE') return '影响 ' + result.affectedRows + ' 行';
    return '已执行';
}

export function markSkipped(results, fromIndex) {
    for (let i = fromIndex; i < results.length; i++) {
        results[i].status = 'skipped';
        results[i].summary = '未执行';
    }
}

function failReason(e) {
    const reason = (e && e.message) ? String(e.message) : '';
    if (!reason) return '执行失败';
    return reason.indexOf('执行失败') === 0 ? reason : '执行失败：' + reason;
}

/** 生产防护锁确认：写操作需输入连接名。取消返回 null，无需确认返回 '' */
export async function promptProdLock(conn) {
    if (!conn || !conn.prodLock) return '';
    try {
        const result = await ElMessageBox.prompt(
            '该连接已开启生产防护锁，请输入连接名确认（写操作需要）', '生产防护锁确认',
            { confirmButtonText: '确定', cancelButtonText: '取消', inputPlaceholder: conn.name });
        return result.value;
    } catch (e) {
        return null;
    }
}

export async function queryStatement(conn, block, sql, confirmText) {
    return request({
        url: '/dbManExec/query',
        method: 'post',
        silent: true,
        data: {
            connId: conn.id,
            sql,
            sqlBlockId: block.id || '',
            database: block.database || '',
            maxRows: 200,
            timeoutSec: 30,
            confirmText: confirmText || ''
        }
    });
}

/** 只重跑某一条语句，就地替换该结果（提交后刷新当前 tab 用） */
export async function refreshStatement(conn, block, index) {
    const st = block.results && block.results[index];
    if (!st) return;
    st.loading = true;
    try {
        const res = await queryStatement(conn, block, st.sql, '');
        st.result = markRaw(res.meta.result);
        st.error = '';
        st.status = 'done';
        st.summary = summarizeResult(res.meta.result);
    } catch (e) {
        st.error = failReason(e);
        st.status = 'error';
        st.summary = '错误';
    } finally {
        st.loading = false;
    }
}

/** 提交结果表格改动：生产防护锁 → 单事务接口 → 成功后只刷新当前语句 */
export async function commitResultChanges(conn, block, index, payload, token) {
    const confirmText = await promptProdLock(conn);
    if (confirmText === null) return false;
    try {
        const res = await request({
            url: '/dbManExec/applyChanges',
            method: 'post',
            silent: true,
            data: {
                token,
                connId: conn.id,
                database: payload.database || block.database || '',
                schema: payload.schema || '',
                table: payload.table,
                confirmText,
                changes: payload.changes
            }
        });
        ElMessage({ message: `提交成功，影响 ${(res.meta && res.meta.affected) || 0} 行`, type: 'success', duration: 2500 });
        await refreshStatement(conn, block, index);
        return true;
    } catch (e) {
        const reason = (e && e.message) ? String(e.message) : '';
        ElMessage({ message: reason || '提交失败', type: 'error', duration: 4000 });
        return false;
    }
}

/** 执行给定 SQL 文本（可能是多条）：校验 → 拆分 → 生产防护确认 → 串行执行 */
export async function executeSql(conn, block, sqlText) {
    const sql = (sqlText || '').trim();
    if (!sql) {
        ElMessage({ message: '请输入 SQL', type: 'warning', duration: 2000 });
        return;
    }
    const allSql = splitSql(sql, { backslashEscapes: conn && conn.dbType === 'mysql' });
    if (allSql.length === 0) {
        ElMessage({ message: '未识别到可执行的 SQL', type: 'warning', duration: 2000 });
        return;
    }
    if (allSql.length > MAX_STATEMENTS) {
        ElMessage({ message: `一次最多执行 ${MAX_STATEMENTS} 条语句（当前 ${allSql.length} 条）`, type: 'warning', duration: 3000 });
        return;
    }
    const confirmText = await promptProdLock(conn);
    if (confirmText === null) return;
    const prev = block.results || [];
    const prevActive = block.activeResult;
    block.results = allSql.map((s, i) => {
        const old = prev[i] && prev[i].sql === s ? prev[i] : null;
        return {
            id: 'st-' + i,
            sql: s,
            result: old ? old.result : null,
            error: '',
            loading: false,
            status: 'pending',
            summary: old ? old.summary : ''
        };
    });
    block.activeResult = block.results.some(r => r.id === prevActive) ? prevActive : block.results[0].id;
    block.loading = true;

    for (let i = 0; i < block.results.length; i++) {
        const st = block.results[i];
        st.status = 'running';
        st.loading = true;
        try {
            const res = await queryStatement(conn, block, st.sql, confirmText);
            st.result = markRaw(res.meta.result);
            st.status = 'done';
            st.summary = summarizeResult(res.meta.result);
        } catch (e) {
            st.error = failReason(e);
            st.status = 'error';
            st.summary = '错误';
            markSkipped(block.results, i + 1);
            break;
        } finally {
            st.loading = false;
        }
    }
    block.loading = false;
}
